import { useState, useCallback } from 'react';
import { getSpineManager } from '../../lib/spine-manager';
import { useSceneStore } from '../../stores/sceneStore';
import type { ViewportState } from '../../lib/spine-manager';

const RULER_SIZE = 20; // must match SceneCanvas

type Bounds = { x: number; y: number; width: number; height: number };

interface Props {
  ids: string[];
  /** Union bounds in PixiJS screen (stage) space — before RULER_SIZE offset */
  bounds: Bounds;
  vpState: ViewportState;
}

interface DragState {
  startClientX: number;
  startClientY: number;
  frozenBounds: Bounds;
  layerStarts: Array<{ id: string; x: number; y: number }>;
}

/**
 * Move-only gizmo for multi-selection.
 * Renders a dashed bounding box around all selected layers and moves them
 * together as a group when dragged.
 */
export default function MultiTransformGizmo({ ids, bounds, vpState }: Props) {
  const { updateLayerTransform, pushUndo } = useSceneStore();
  const [drag, setDrag] = useState<DragState | null>(null);
  const [displayBounds, setDisplayBounds] = useState<Bounds | null>(null);
  const { zoom } = vpState;

  const visibleBounds = displayBounds ?? bounds;
  const L = visibleBounds.x + RULER_SIZE;
  const T = visibleBounds.y + RULER_SIZE;
  const W = Math.max(visibleBounds.width, 4);
  const H = Math.max(visibleBounds.height, 4);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);

      const layers = useSceneStore.getState().layers;
      // Snapshot start positions for all non-locked selected layers
      const layerStarts = ids.flatMap((id) => {
        const layer = layers.find((l) => l.id === id);
        if (!layer || layer.locked) return [];
        return [{ id, x: layer.transform.x, y: layer.transform.y }];
      });

      pushUndo();
      getSpineManager().pauseViewportDrag();
      setDisplayBounds(bounds);
      setDrag({ startClientX: e.clientX, startClientY: e.clientY, frozenBounds: bounds, layerStarts });
    },
    [ids, bounds, pushUndo],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!drag) return;
      const dxScreen = e.clientX - drag.startClientX;
      const dyScreen = e.clientY - drag.startClientY;
      const dx = dxScreen / zoom;
      const dy = dyScreen / zoom;

      // Shift the display box immediately for responsive feedback
      setDisplayBounds({
        x: drag.frozenBounds.x + dxScreen,
        y: drag.frozenBounds.y + dyScreen,
        width: drag.frozenBounds.width,
        height: drag.frozenBounds.height,
      });

      // Apply delta to each layer
      drag.layerStarts.forEach(({ id, x, y }) => {
        updateLayerTransform(id, { x: x + dx, y: y + dy });
      });
    },
    [drag, zoom, updateLayerTransform],
  );

  const onPointerUp = useCallback(() => {
    if (!drag) return;
    getSpineManager().resumeViewportDrag();
    setDrag(null);
    setDisplayBounds(null);
  }, [drag]);

  return (
    <div
      data-testid="multi-transform-gizmo"
      style={{
        position: 'absolute',
        left: L,
        top: T,
        width: W,
        height: H,
        cursor: drag ? 'grabbing' : 'move',
        touchAction: 'none',
        zIndex: 20,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* Dashed selection rectangle */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          border: '2px dashed #7c6af7',
          borderRadius: 2,
          pointerEvents: 'none',
        }}
      />
      {/* Corner accent dots */}
      {[
        { top: -3, left: -3 },
        { top: -3, right: -3 },
        { bottom: -3, left: -3 },
        { bottom: -3, right: -3 },
      ].map((pos, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: 6,
            height: 6,
            background: '#7c6af7',
            borderRadius: 1,
            pointerEvents: 'none',
            ...pos,
          }}
        />
      ))}
    </div>
  );
}
